/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  fetchLibraryResourceStats,
  type LibraryResourceStats,
} from '../api/library-resource-stats';

export interface LibraryResourceMetrics {
  total: number;
  publishedCount: number;
  draftCount: number;
  knowledgeCount: number;
  pluginCount: number;
  workflowCount: number;
  imageflowCount: number;
  promptCount: number;
  loading: boolean;
}

const EMPTY_STATS: LibraryResourceStats = {
  total: 0,
  published_count: 0,
  draft_count: 0,
  knowledge_count: 0,
  plugin_count: 0,
  workflow_count: 0,
  imageflow_count: 0,
  prompt_count: 0,
};

function toMetrics(
  stats: LibraryResourceStats,
  loading: boolean,
): LibraryResourceMetrics {
  return {
    total: stats.total,
    publishedCount: stats.published_count,
    draftCount: stats.draft_count,
    knowledgeCount: stats.knowledge_count,
    pluginCount: stats.plugin_count,
    workflowCount: stats.workflow_count,
    imageflowCount: stats.imageflow_count,
    promptCount: stats.prompt_count,
    loading,
  };
}

export function useLibraryResourceMetrics(spaceId?: string) {
  const query = useQuery({
    queryKey: ['library-resource-stats', spaceId],
    queryFn: () => fetchLibraryResourceStats(spaceId ?? ''),
    enabled: Boolean(spaceId),
    staleTime: 60 * 1000,
  });

  return useMemo(
    () =>
      toMetrics(query.data ?? EMPTY_STATS, query.isLoading && Boolean(spaceId)),
    [query.data, query.isLoading, spaceId],
  );
}
